/**
 * Capture transform for WidgetAskAI.
 *
 * Two width-related problems on this widget:
 *  1. The markdown answer (rendered via react-markdown into <p> / <h*> /
 *     <table> / <li>) has no max-width. The generic flow sets
 *     `min-width: max-content` on the outer container so charts can render
 *     past their scroll box — that strips the only soft-wrap constraint and
 *     each paragraph lays itself out on a single line, blowing past the
 *     16384px canvas ceiling.
 *  2. DirectLinkRefs renders source citations in a horizontal-scroll strip
 *     (flex row, overflow-x: auto, each card flex-shrink: 0 with a min-width).
 *     The generic flow forces overflow: visible, and then max-content
 *     expansion lays every card out on one row instead of wrapping.
 *
 * Fix, scoped to this widget: cap block elements to the widget's natural
 * rendered width and let the references strip wrap onto multiple rows.
 *
 * Detection note: by the time this runs, the generic flow has already
 * overwritten any inline `overflow-x: auto` to `visible`, so the references
 * strip is identified by structural signature (flex row whose children are
 * flex-shrink: 0 with a min-width).
 */

export const prepareAskAI = async (containerElement) => {
  const restorations = [];

  const naturalWidth = Math.max(
    400,
    Math.floor(containerElement.getBoundingClientRect().width) - 32
  );

  // ── 1. Cap markdown block elements so paragraphs soft-wrap ───────────────
  const blockSel = "p, h1, h2, h3, h4, h5, h6, li, ul, ol, table, blockquote, pre";
  containerElement.querySelectorAll(blockSel).forEach((el) => {
    const prev = {
      maxWidth: el.style.maxWidth,
      width: el.style.width,
      whiteSpace: el.style.whiteSpace,
      overflowWrap: el.style.overflowWrap,
      wordBreak: el.style.wordBreak,
    };
    el.style.maxWidth = `${naturalWidth}px`;
    el.style.whiteSpace = "normal";
    el.style.overflowWrap = "break-word";
    el.style.wordBreak = "break-word";
    restorations.push(() => Object.assign(el.style, prev));
  });

  // ── 2. Let horizontal-scroll reference strips wrap to multiple rows ──────
  containerElement.querySelectorAll("div").forEach((el) => {
    const cs = window.getComputedStyle(el);
    if (cs.display !== "flex") return;
    if (cs.flexDirection === "column" || cs.flexDirection === "column-reverse") return;

    const children = Array.from(el.children);
    if (children.length < 2) return;

    const isScrollStrip = children.some((child) => {
      const ccs = window.getComputedStyle(child);
      return ccs.flexShrink === "0" && parseFloat(ccs.minWidth) > 100;
    });
    if (!isScrollStrip) return;

    const prev = {
      flexWrap: el.style.flexWrap,
      maxWidth: el.style.maxWidth,
    };
    el.style.flexWrap = "wrap";
    el.style.maxWidth = `${naturalWidth}px`;
    restorations.push(() => Object.assign(el.style, prev));
  });

  return () => {
    while (restorations.length) restorations.pop()();
  };
};
